import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { setUnauthorizedHandler } from '../api/client'
import { authApi } from '../api/endpoints'
import { tokenStore } from '../api/tokens'
import type { Role, User } from '../api/types'
import { isTranscoder, roleAtLeast } from './roles'

type Status = 'loading' | 'authenticated' | 'anonymous'

interface AuthState {
  status: Status
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  can: (min: Role) => boolean
  isTranscoder: boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<Status>(() => (tokenStore.get() ? 'loading' : 'anonymous'))

  const logout = useCallback(() => {
    tokenStore.clear()
    queryClient.clear()
    setUser(null)
    setStatus('anonymous')
  }, [queryClient])

  useEffect(() => {
    setUnauthorizedHandler(logout)
  }, [logout])

  useEffect(() => {
    if (status !== 'loading') return
    let cancelled = false
    authApi
      .me()
      .then((u) => {
        if (cancelled) return
        setUser(u)
        setStatus('authenticated')
      })
      .catch(() => {
        // A 401 already cleared the session via the unauthorized handler. Any
        // other failure (network, 5xx, fetch aborted by navigation) must not
        // wipe stored tokens: show the login screen but let a reload recover.
        if (cancelled) return
        setUser(null)
        setStatus('anonymous')
      })
    return () => {
      cancelled = true
    }
  }, [status, logout])

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password)
    tokenStore.set(res)
    setUser(res.user)
    setStatus('authenticated')
  }, [])

  const value = useMemo<AuthState>(
    () => ({ status, user, login, logout, can: (min: Role) => roleAtLeast(user?.role, min), isTranscoder: isTranscoder(user?.role) }),
    [status, user, login, logout],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
